// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useRouter } from 'expo-router';
import { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import {
  overviewTotals,
  useAnalysisOverview,
  type AnalysisOverviewRow,
} from '@/features/fleet/useAnalysisOverview';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * What the estate has analysed, and what came of it — every course on one screen.
 *
 * The course panel answers "how did this course go" and the fleet page answers "what have the
 * machines been doing". Neither answered "what have we analysed, and what did we get", which
 * meant opening courses one at a time or querying the database by hand.
 *
 * Developer-or-admin gated, same as the fleet page: this is the estate's operational view
 * rather than any one course's data.
 */
export default function AnalysisOverviewScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const router = useRouter();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { state, refresh } = useAnalysisOverview(apiBaseUrl, user?.token, canView);

  const totals = state.status === 'ready' ? overviewTotals(state.courses) : null;

  return (
    <>
      <Stack.Screen options={{ title: t('analysisOverviewPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('analysisOverviewPage.title')}</Text>
        <Text style={styles.intro}>{t('analysisOverviewPage.intro')}</Text>

        {!canView || state.status === 'forbidden' ? (
          <Text style={styles.paragraph} testID="analysis-overview-forbidden">
            {t('analysisOverviewPage.forbidden')}
          </Text>
        ) : state.status === 'loading' ? (
          <Text style={styles.paragraph}>{t('analysisOverviewPage.loading')}</Text>
        ) : state.status === 'failed' ? (
          <>
            <Text style={styles.paragraph} testID="analysis-overview-failed">
              {t('analysisOverviewPage.failed')}
            </Text>
            <Pressable style={styles.button} onPress={refresh} accessibilityRole="button">
              <Text style={styles.buttonText}>{t('analysisOverviewPage.retry')}</Text>
            </Pressable>
          </>
        ) : (
          <>
            <View style={styles.summaryRow}>
              <Text style={styles.summary} testID="analysis-overview-summary">
                {t('analysisOverviewPage.summary', {
                  analysed: totals!.analysed,
                  courses: totals!.courses,
                  features: totals!.features,
                  pending: totals!.pendingReview,
                })}
              </Text>
              <Pressable
                style={styles.button}
                onPress={refresh}
                accessibilityRole="button"
                testID="analysis-overview-refresh"
              >
                <Text style={styles.buttonText}>{t('analysisOverviewPage.refresh')}</Text>
              </Pressable>
            </View>

            {state.courses.length === 0 ? (
              <Text style={styles.paragraph} testID="analysis-overview-empty">
                {t('analysisOverviewPage.empty')}
              </Text>
            ) : (
              state.courses.map((course) => (
                <CourseRow
                  key={course.courseId}
                  course={course}
                  t={t}
                  onOpen={() => router.push(`/course-details?courseId=${course.courseId}`)}
                />
              ))
            )}
          </>
        )}
      </ScrollView>
    </>
  );
}

function CourseRow({
  course,
  t,
  onOpen,
}: {
  course: AnalysisOverviewRow;
  t: Translate;
  onOpen: () => void;
}) {
  // Three states worth telling apart at a glance: analysed, tried and failed, never run. A
  // course with a request and no run is queued — the analyser has not reached it yet.
  const tone =
    course.lastAnalysedAt !== null
      ? 'good'
      : course.failedRuns > 0
        ? 'bad'
        : 'warn';

  return (
    <Pressable
      style={styles.card}
      onPress={onOpen}
      accessibilityRole="button"
      testID="analysis-overview-row"
    >
      <View style={styles.cardHeaderRow}>
        <View style={[styles.dot, tone === 'good' ? styles.dotGood : tone === 'bad' ? styles.dotBad : styles.dotWarn]} />
        <Text style={styles.courseName}>{course.courseName}</Text>
        <Text style={styles.when}>
          {course.lastAnalysedAt
            ? formatWhen(course.lastAnalysedAt)
            : course.requestedAt
              ? t('analysisOverviewPage.queued')
              : t('analysisOverviewPage.neverAnalysed')}
        </Text>
      </View>

      <View style={styles.chipRow}>
        <Chip label={t('analysisOverviewPage.runs', { count: course.runs })} />
        {course.failedRuns > 0 ? (
          <Chip label={t('analysisOverviewPage.failedRuns', { count: course.failedRuns })} bad />
        ) : null}
        {course.modelRuns > 0 ? (
          <Chip label={t('analysisOverviewPage.modelRuns', { count: course.modelRuns })} />
        ) : null}
        <Chip
          label={t('analysisOverviewPage.features', {
            total: course.features,
            satellite: course.satelliteFeatures,
          })}
        />
        {course.pendingReview > 0 ? (
          <Chip label={t('analysisOverviewPage.pending', { count: course.pendingReview })} />
        ) : null}
      </View>
    </Pressable>
  );
}

function Chip({ label, bad = false }: { label: string; bad?: boolean }) {
  return (
    <View style={[styles.chip, bad ? styles.chipBad : null]}>
      <Text style={[styles.chipText, bad ? styles.chipTextBad : null]}>{label}</Text>
    </View>
  );
}

const formatWhen = (iso: string): string => {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? iso : new Date(parsed).toLocaleString();
};

const styles = StyleSheet.create({
  container: { padding: 16, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, marginBottom: 12, lineHeight: 18 },
  paragraph: { color: appColors.text.secondary, marginBottom: 12 },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
  },
  summary: { color: appColors.text.secondary, fontSize: 13, flexShrink: 1 },
  button: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignSelf: 'flex-start',
  },
  buttonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 12 },
  card: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    padding: 12,
    marginBottom: 8,
    gap: 8,
  },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  dotGood: { backgroundColor: appColors.status.success },
  dotWarn: { backgroundColor: appColors.status.warning },
  dotBad: { backgroundColor: appColors.status.danger },
  courseName: { color: appColors.text.primary, fontWeight: '600', fontSize: 15, flexShrink: 1 },
  when: { color: appColors.text.muted, fontSize: 12, marginLeft: 'auto' },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: {
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  chipBad: { borderColor: appColors.status.danger },
  chipText: { color: appColors.text.secondary, fontSize: 12 },
  chipTextBad: { color: appColors.status.danger },
});
